import React, { useEffect } from 'react';
import { calculateDurationBetweenDates } from '@/lib/helpers/durationHelpers';
import { useTranslation } from 'react-i18next';

interface Dates {
    past?: Date | string;
    future?: Date | string;
}

type Options = {
    long?: boolean;
    locale?: string;
    units?: string[];
    showJustBiggestUnit?: boolean;
    maxUnitsCount?: number;
    utc?: boolean;
};

const defaultOptions: Options = {
    long: true,
    locale: 'en',
    units: ['years', 'months', 'days', 'hours', 'minutes', 'seconds'],
    showJustBiggestUnit: true,
};

const useCountdownLocalized = ({
    date1,
    date2 = null,
    callback,
    options = defaultOptions,
}: {
    date1: string | Date;
    date2?: string | Date | null;
    callback?: () => void;
    options?: Options;
}): Dates => {
    const [isCountDownExpired, setCountDownExpired] = React.useState(false);
    const { t, i18n } = useTranslation();
    const [countDown, setCountDown] = React.useState<Dates>({
        past: '',
        future: '',
    });
    const [dates, setDates] = React.useState<{
        past?: Date;
        future?: Date;
    }>({
        past: undefined,
        future: undefined,
    });

    const compareWithCurrentDate = (
        date1: string | Date,
        date2: string | Date | null = null,
        options: Options,
    ) => {
        try {
            if (!date1 && !date2) {
                return { past: new Date(), future: new Date() };
            }
            const d1 = typeof date1 === 'string' ? new Date(date1 || 0) : date1;
            let d2 = typeof date2 === 'string' ? new Date(date2 || 0) : date2;
            if (d2 === null) d2 = new Date();

            if (isNaN(d1.getTime()) || isNaN(d2.getTime())) {
                throw new Error('Invalid date provided');
            }

            let currentDate = new Date();

            if (options.utc) {
                currentDate = new Date(currentDate.toUTCString());
            }

            if (
                new Date(dates.future || 0).getTime() >= d1.getTime() ||
                new Date(dates.future || 0).getTime() >= d2.getTime()
            ) {
                setDates({ future: undefined, past: undefined });
                setCountDown({ past: '', future: '' });
            }
            const result: { past?: Date; future?: Date } = {};
            if (d1.getTime() < currentDate.getTime()) {
                result.past = d1;
            } else {
                result.future = d1;
            }
            if (d2.getTime() < currentDate.getTime()) {
                result.past = result.past || d2;
            } else {
                result.future = result.future || d2;
            }
            return result;
        } catch (error) {
            console.error(error);
            return { past: new Date(), future: new Date() };
        }
    };

    const updateCountdown = () => {
        if (dates.past) {
            const durationFromPast = calculateDurationBetweenDates(
                new Date(dates.past),
                new Date(),
                { ...options, locale: i18n.language || 'en' },
            );
            setCountDown((prevState) => ({
                ...prevState,
                past: `
            ${durationFromPast} ${t('ago_label')}`,
            }));
        }
        if (dates.future && dates.future.getTime() > new Date().getTime()) {
            const durationToFuture = calculateDurationBetweenDates(
                new Date(dates.future),
                new Date(),
                { ...options, locale: i18n.language || 'en' },
            );
            setCountDown((prevState) => ({ ...prevState, future: durationToFuture }));
        } else {
            setCountDownExpired((prev) => (prev ? prev : true));
        }
    };

    useEffect(() => {
        setDates(compareWithCurrentDate(date1, date2, options));
    }, [date1, date2]);

    useEffect(() => {
        setCountDownExpired(false);
        let interval: ReturnType<typeof setInterval>;
        if (dates.past || dates.future) {
            interval = setInterval(updateCountdown, 1000);
        }
        return () => clearInterval(interval);
    }, [dates]);

    useEffect(() => {
        if (isCountDownExpired && callback) {
            callback();
        }
    }, [isCountDownExpired, callback]);

    return countDown;
};

export default useCountdownLocalized;
